import { useState } from 'react';
import { ChevronDown, ChevronUp, Route } from 'lucide-react';

const DEFAULT_COLLAPSED = 4;
const SCROLL_THRESHOLD = 6;

interface TransitStopRouteListProps {
  routes: string[];
  collapsedLimit?: number;
}

/** Compact route list — long names read better as rows than wrapping chips. */
export function TransitStopRouteList({
  routes,
  collapsedLimit = DEFAULT_COLLAPSED,
}: TransitStopRouteListProps) {
  const [expanded, setExpanded] = useState(false);

  if (!routes.length) return null;

  const needsCollapse = routes.length > collapsedLimit;
  const visible = expanded || !needsCollapse ? routes : routes.slice(0, collapsedLimit);
  const hiddenCount = routes.length - collapsedLimit;
  const scrollable = expanded && routes.length > SCROLL_THRESHOLD;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Routes served
        </p>
        <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums shrink-0">
          {routes.length} {routes.length === 1 ? 'route' : 'routes'}
        </span>
      </div>
      <ul
        className={`rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40 divide-y divide-gray-100 dark:divide-gray-700/80 ${
          scrollable ? 'max-h-44 overflow-y-auto overscroll-contain' : ''
        }`}
      >
        {visible.map((route) => (
          <li key={route} className="flex items-start gap-2.5 px-3 py-2">
            <Route
              size={13}
              className="mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400"
              aria-hidden
            />
            <span
              className="text-sm text-gray-800 dark:text-gray-200 leading-snug break-words min-w-0"
              title={route}
            >
              {route}
            </span>
          </li>
        ))}
      </ul>
      {needsCollapse && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:underline"
        >
          {expanded ? (
            <>
              <ChevronUp size={14} />
              Show fewer routes
            </>
          ) : (
            <>
              <ChevronDown size={14} />
              Show {hiddenCount} more route{hiddenCount === 1 ? '' : 's'}
            </>
          )}
        </button>
      )}
    </div>
  );
}

interface TransitStopOperatorListProps {
  operators: string[];
  collapsedLimit?: number;
}

/** Shorter operator names — chips with optional collapse. */
export function TransitStopOperatorList({
  operators,
  collapsedLimit = 5,
}: TransitStopOperatorListProps) {
  const [expanded, setExpanded] = useState(false);

  if (!operators.length) return null;

  const needsCollapse = operators.length > collapsedLimit;
  const visible = expanded || !needsCollapse ? operators : operators.slice(0, collapsedLimit);
  const hiddenCount = operators.length - collapsedLimit;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Operators
        </p>
        {operators.length > 1 && (
          <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums shrink-0">
            {operators.length}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((op) => (
          <span
            key={op}
            className="inline-flex max-w-full px-2.5 py-0.5 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 truncate"
            title={op}
          >
            {op}
          </span>
        ))}
      </div>
      {needsCollapse && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-400 hover:underline"
        >
          {expanded ? (
            <>
              <ChevronUp size={14} />
              Show fewer
            </>
          ) : (
            <>
              <ChevronDown size={14} />
              +{hiddenCount} more
            </>
          )}
        </button>
      )}
    </div>
  );
}
